import { useEffect, useMemo, useRef, useState } from "react";
import { CARD_PIXEL_SIZE, GRID_SIZE } from "../../constants/editor";
import { composeCardGrid } from "../../lib/card-scene";
import { getFrameColor, getFrameWeightPx } from "../../lib/card";
import type { CardDraft } from "../../shared/game";
import { PixelEditorCanvas } from "../canvas/PixelEditorCanvas";
import { FrameOverlay } from "./FrameOverlay";

type CardDesignerProps = {
  card: CardDraft;
  canEdit: boolean;
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onPointerStart: (row: number, col: number) => void;
  onPointerMove: (row: number, col: number) => void;
  onPointerEnd: () => void;
};

export function CardDesigner({
  card,
  canEdit,
  onNameChange,
  onDescriptionChange,
  onPointerStart,
  onPointerMove,
  onPointerEnd,
}: CardDesignerProps) {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [cardSize, setCardSize] = useState<[number, number]>([386, 580]);
  const frameColor = getFrameColor(card);
  const frameWeightPx = getFrameWeightPx(card);
  const typeColor = getFrameColor({ ...card, frameColorManual: null });
  const composedGrid = useMemo(() => composeCardGrid(card), [card]);

  useEffect(() => {
    const element = cardRef.current;
    if (!element) {
      return;
    }

    const update = () => {
      const bounds = element.getBoundingClientRect();
      setCardSize([bounds.width, bounds.height]);
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const pixelCanvasSize = useMemo(() => GRID_SIZE * CARD_PIXEL_SIZE, []);

  return (
    <div className="card-designer-shell">
      <div
        ref={cardRef}
        className="card-shell"
        style={{
          width: pixelCanvasSize + 32,
          background: "linear-gradient(160deg,#14102a 0%,#09070f 55%,#0c1120 100%)",
          border: `${frameWeightPx}px solid ${frameColor}`,
          borderRadius: 22,
          padding: 14,
          position: "relative",
          overflow: "hidden",
          boxShadow: `0 0 0 1px ${frameColor}25, 0 14px 44px rgba(0,0,0,0.55)`,
        }}
      >
        <div style={{ position: "absolute", inset: 0, borderRadius: 20, background: `linear-gradient(120deg,${frameColor}0d 0%,transparent 30%,${frameColor}08 52%,transparent 70%,${frameColor}0d 100%)`, pointerEvents: "none" }} />
        <FrameOverlay frameStyle={card.frameStyle} color={frameColor} width={cardSize[0]} height={cardSize[1]} />

        <div style={{ position: "relative", zIndex: 2, display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, gap: 12 }}>
          <input
            disabled={!canEdit}
            value={card.name}
            onChange={(event) => onNameChange(event.target.value)}
            maxLength={18}
            className="card-name-input"
            placeholder="Card name"
          />
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: typeColor, boxShadow: `0 0 8px ${typeColor}` }} />
            <div style={{ padding: "4px 8px", borderRadius: 8, border: `1px solid ${typeColor}77`, color: typeColor, background: `${typeColor}18`, fontSize: 10, fontWeight: 700 }}>
              HP {card.hp}
            </div>
          </div>
        </div>

        <div style={{ position: "relative", zIndex: 2, borderRadius: 12, overflow: "hidden", border: `2px solid ${typeColor}33`, boxShadow: "inset 0 0 20px rgba(0,0,0,0.4)" }}>
          <PixelEditorCanvas
            grid={composedGrid}
            size={pixelCanvasSize}
            onPointerStart={onPointerStart}
            onPointerMove={onPointerMove}
            onPointerEnd={onPointerEnd}
          />
        </div>

        <div style={{ position: "relative", zIndex: 2, marginTop: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7 }}>
            <div style={{ flex: 1, height: 1, background: `linear-gradient(to right,${typeColor}55,transparent)` }} />
            <div style={{ fontSize: 8, color: `${typeColor}99`, letterSpacing: 1 }}>DESCRIPTION</div>
            <div style={{ flex: 1, height: 1, background: `linear-gradient(to left,${typeColor}55,transparent)` }} />
          </div>
          <textarea
            disabled={!canEdit}
            value={card.description}
            onChange={(event) => onDescriptionChange(event.target.value)}
            placeholder="Describe your monster's powers, origin, or special abilities."
            className="card-description-input"
            rows={3}
            maxLength={180}
          />
        </div>

        <div style={{ position: "relative", zIndex: 2, marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ padding: "4px 8px", borderRadius: 8, border: `1px solid ${typeColor}66`, color: typeColor, background: `${typeColor}18`, fontSize: 10, fontWeight: 700 }}>
            {card.typeLabel}
          </div>
          <div style={{ display: "flex", gap: 5 }}>
            {Array.from({ length: 5 }, (_, index) => (
              <div key={index} style={{ width: index === 2 ? 8 : 5, height: index === 2 ? 8 : 5, borderRadius: "50%", background: index === 2 ? typeColor : `${typeColor}44`, boxShadow: index === 2 ? `0 0 7px ${typeColor}` : "none" }} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
